import React, { useMemo, useState } from 'react';
import {
  BrioModal,
  BrioModalBody,
  BrioModalField,
  BrioInput,
  BrioSelect,
  BrioButton,
} from '../../components/BrioComponents';

function flattenZoneOptions(tree, depth = 0, acc = []) {
  tree.forEach((z) => {
    acc.push({ id: z.id, label: `${'— '.repeat(depth)}${z.code} · ${z.label}` });
    if (z.children?.length) flattenZoneOptions(z.children, depth + 1, acc);
  });
  return acc;
}

function AddProjectItemModal({ versions, zoneTree, onClose, onCreate }) {
  const [search, setSearch] = useState('');
  const [itemVersionId, setItemVersionId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [projectZoneId, setProjectZoneId] = useState('');
  const [descriptionOverride, setDescriptionOverride] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const zoneOptions = useMemo(() => flattenZoneOptions(zoneTree || []), [zoneTree]);

  const filteredVersions = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = versions || [];
    if (!q) return list.slice(0, 20);
    return list
      .filter((v) =>
        v.fullCode.toLowerCase().includes(q) ||
        v.shortDescription?.toLowerCase().includes(q) ||
        v.unit?.toLowerCase().includes(q)
      )
      .slice(0, 30);
  }, [versions, search]);

  const selectedVersion = useMemo(
    () => versions.find((v) => v.id === itemVersionId) || null,
    [versions, itemVersionId]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!itemVersionId) { setError('Pick a library item.'); return; }
    if (quantity === '' || isNaN(Number(quantity))) { setError('Quantity must be a number.'); return; }
    if (Number(quantity) < 0) { setError('Quantity cannot be negative.'); return; }
    setSubmitting(true);
    const result = await onCreate({
      itemVersionId,
      quantity: Number(quantity),
      projectZoneId: projectZoneId || null,
      descriptionOverride,
    });
    setSubmitting(false);
    if (result.success) onClose();
    else setError(result.error || 'Failed to add line.');
  };

  const footer = (
    <>
      <BrioButton variant="secondary" onClick={onClose} disabled={submitting}>Cancel</BrioButton>
      <BrioButton variant="primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Adding…' : 'Add line'}
      </BrioButton>
    </>
  );

  return (
    <BrioModal title="Add BOQ line" onClose={onClose} width="lg" footer={footer}>
      <BrioModalBody form>
        {error && (
          <div style={{ padding: '0.75rem 1rem', background: '#fdecec', color: '#a94442', borderRadius: 6, marginBottom: '1rem' }}>
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <BrioModalField label="Search library *" hint="Match by code, description or unit. Pick a version below.">
            <BrioInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="MA.EXT or brickwork or m³"
              autoFocus
            />
          </BrioModalField>
          <div style={{
            border: '1px solid var(--brio-border, #ccc)',
            borderRadius: 6,
            maxHeight: 240,
            overflowY: 'auto',
            marginBottom: '1rem',
          }}>
            {filteredVersions.length === 0 ? (
              <div style={{ padding: '1rem', color: 'var(--brio-text-muted)', fontSize: '0.85rem' }}>
                {versions.length === 0
                  ? 'Library is empty — add items in the Library page first.'
                  : 'No versions match the search.'}
              </div>
            ) : (
              filteredVersions.map((v) => {
                const active = v.id === itemVersionId;
                return (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => setItemVersionId(v.id)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '130px 1fr 70px',
                      gap: '0.6rem',
                      width: '100%',
                      padding: '0.5rem 0.75rem',
                      border: 'none',
                      background: active ? 'var(--brio-gold-faint, #f7f1d4)' : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      borderBottom: '1px solid var(--brio-border-subtle, #eee)',
                      fontSize: '0.85rem',
                    }}
                  >
                    <code>{v.fullCode}</code>
                    <span>{v.shortDescription}</span>
                    <span style={{ color: 'var(--brio-text-muted)' }}>{v.unit}</span>
                  </button>
                );
              })
            )}
          </div>
          {selectedVersion && (
            <div style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem', background: 'var(--brio-surface-muted, #f7f7f5)', borderRadius: 6, marginBottom: '1rem' }}>
              Selected: <code>{selectedVersion.fullCode}</code> — {selectedVersion.shortDescription} ({selectedVersion.unit})
            </div>
          )}

          <BrioModalField label="Quantity *">
            <BrioInput
              type="number"
              min="0"
              step="any"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="0"
            />
          </BrioModalField>
          <BrioModalField label="Zone" hint="Optional — pin this line to a specific zone.">
            <BrioSelect value={projectZoneId} onChange={(e) => setProjectZoneId(e.target.value)}>
              <option value="">— Unzoned —</option>
              {zoneOptions.map((z) => (
                <option key={z.id} value={z.id}>{z.label}</option>
              ))}
            </BrioSelect>
          </BrioModalField>
          <BrioModalField label="Description override" hint="Optional — use only when the library description doesn't fit. Prefer creating a new item version.">
            <textarea
              value={descriptionOverride}
              onChange={(e) => setDescriptionOverride(e.target.value)}
              className="brio-modal-input"
              rows={2}
            />
          </BrioModalField>
        </form>
      </BrioModalBody>
    </BrioModal>
  );
}

export default AddProjectItemModal;
